import Logger from '../fw/logger';


export default class MediaSessionMetadata {

  #rmp;
  #setMediaSessionContentMetadataFn = null;
  #updatePositionStateFn = null;

  constructor(rmp) {
    this.#rmp = rmp;
  }

  static playbackStatePlaying() {
    navigator.mediaSession.playbackState = 'playing';
  }

  static playbackStatePaused() {
    navigator.mediaSession.playbackState = 'paused';
  }

  static destroy() {
    if (typeof window.navigator.mediaSession !== 'undefined') {
      try {
        navigator.mediaSession.setActionHandler('play', null);
        navigator.mediaSession.setActionHandler('stop', null);
        navigator.mediaSession.setActionHandler('pause', null);
        navigator.mediaSession.setActionHandler('seekto', null);
        navigator.mediaSession.setActionHandler('seekbackward', null);
        navigator.mediaSession.setActionHandler('seekforward', null);
        navigator.mediaSession.setActionHandler('previoustrack', null);
        navigator.mediaSession.setActionHandler('nexttrack', null);
        navigator.mediaSession.setPositionState(null);
      } catch (warning) {
        console.warn(warning);
      }
    }
  }

  #setMediaSessionContentMetadata() {
    const video = this.#rmp.dom.video;
    navigator.mediaSession.playbackState = 'playing';
    this.updatePositionState();
    this.updateMetadata();
    video.addEventListener('play', MediaSessionMetadata.playbackStatePlaying);
    video.addEventListener('pause', MediaSessionMetadata.playbackStatePaused);
    this.#updatePositionStateFn = this.updatePositionState.bind(this);
    video.addEventListener('ratechange', this.#updatePositionStateFn);
    video.addEventListener('timeupdate', this.#updatePositionStateFn);
    // we cannot know for sure which action is supported so 
    // we need to try/catch https://github.com/w3c/mediasession/issues/228
    // typically stop fails on Samsung TVs
    try {
      navigator.mediaSession.setActionHandler('seekbackward', () => {
        const currentTime = this.#rmp.currentTime;
        if (currentTime >= 0) {
          this.#rmp.seekTo(currentTime - 10000).catch(error => {
            this.#rmp.rmpUtils.warning(1016, error);
          });
        }
        this.updatePositionState();
      });
      navigator.mediaSession.setActionHandler('seekforward', () => {
        const currentTime = this.#rmp.currentTime;
        if (currentTime >= 0) {
          this.#rmp.seekTo(currentTime + 10000).catch(error => {
            this.#rmp.rmpUtils.warning(1016, error);
          });
        }
        this.updatePositionState();
      });
      navigator.mediaSession.setActionHandler('seekto', (details) => {
        this.#rmp.seekTo(details.seekTime * 1000).catch(error => {
          this.#rmp.rmpUtils.warning(1016, error);
        });
        this.updatePositionState();
      });
      navigator.mediaSession.setActionHandler('play', () => {
        this.#rmp.play().catch(error => {
          this.#rmp.rmpUtils.warning(1014, error);
        });
      });
      navigator.mediaSession.setActionHandler('stop', () => {
        this.#rmp.stop().catch(error => {
          this.#rmp.rmpUtils.warning(1015, error);
        });
      });
      navigator.mediaSession.setActionHandler('pause', () => {
        this.#rmp.pause();
      });
      if (this.#rmp.hasPlaylist || this.#rmp.hasRelated) {
        navigator.mediaSession.setActionHandler('previoustrack', () => {
          if (this.#rmp.hasPlaylist) {
            const currentItemIndex = this.#rmp.getPlaylistItem();
            this.#rmp.setPlaylistItem(currentItemIndex - 1).catch(error => {
              this.#rmp.rmpUtils.warning(1023, error);
            });
          } else if (this.#rmp.hasRelated) {
            const currentItemIndex = this.#rmp.getCurrentRelatedItemIndex();
            this.#rmp.setRelatedItem(currentItemIndex - 1).catch(error => {
              this.#rmp.rmpUtils.warning(1024, error);
            });
          }
        });
        navigator.mediaSession.setActionHandler('nexttrack', () => {
          if (this.#rmp.hasPlaylist) {
            const currentItemIndex = this.#rmp.getPlaylistItem();
            this.#rmp.setPlaylistItem(currentItemIndex + 1).catch(error => {
              this.#rmp.rmpUtils.warning(1023, error);
            });
          } else if (this.#rmp.hasRelated) {
            const currentItemIndex = this.#rmp.getCurrentRelatedItemIndex();
            this.#rmp.setRelatedItem(currentItemIndex + 1).catch(error => {
              this.#rmp.rmpUtils.warning(1024, error);
            });
          }
        });
      }
    } catch (warning) {
      console.warn(warning);
    }
  }

  updatePositionState() {
    const video = this.#rmp.dom.video;
    if ('setPositionState' in navigator.mediaSession) {
      const playbackRate = video.playbackRate;
      if (playbackRate === 0) {
        return;
      }
      const duration = video.duration;
      const position = video.currentTime;
      if (!Number.isFinite(duration) || !Number.isFinite(position)) {
        return;
      }
      if (DEBUG) {
        if (this.#rmp.debugLevel === 2) {
          Logger.print('info', 'mediaSession setPositionState duration: ' + duration +
            ' - playbackRate: ' + playbackRate +
            ' - position: ' + position);
        }
      }
      try {
        navigator.mediaSession.setPositionState({
          duration,
          playbackRate,
          position
        });
      } catch (warning) {
        console.warn(warning);
      }
    }
  }

  updateMetadata() {
    const contentMetadata = this.#rmp.contentMetadataConfiguration;
    const validMetadataProperties = ['title', 'artist', 'album', 'artwork'];
    const validMetadata = {};
    validMetadataProperties.forEach(prop => {
      if (prop !== 'artwork' && contentMetadata[prop]) {
        validMetadata[prop] = contentMetadata[prop];
      } else if (Array.isArray(contentMetadata[prop]) && contentMetadata[prop].length > 0) {
        validMetadata[prop] = contentMetadata[prop];
      }
    });
    Logger.print('info', 'mediaSession Metadata', validMetadata);
    navigator.mediaSession.metadata = new window.MediaMetadata(validMetadata);
  }

  unwire() {
    const video = this.#rmp.dom.video;
    video.removeEventListener('play', MediaSessionMetadata.playbackStatePlaying);
    video.removeEventListener('pause', MediaSessionMetadata.playbackStatePaused);
    video.removeEventListener('playing', this.#setMediaSessionContentMetadataFn);
    video.removeEventListener('ratechange', this.#updatePositionStateFn);
    video.removeEventListener('timeupdate', this.#updatePositionStateFn);
  }

  init() {
    this.#setMediaSessionContentMetadataFn = this.#setMediaSessionContentMetadata.bind(this);
    this.#rmp.dom.video.addEventListener('playing', this.#setMediaSessionContentMetadataFn, { once: true });
  }

}
